/**
 * AR Bad-Debt Write-Off — Phase polish.
 *
 * Formal workflow for clearing uncollectable customer balances. Posts:
 *   DR 6900 Bad Debt Expense (or 6900-class operating expense)
 *   CR 1100 Accounts Receivable (subledger: customer)
 *
 * Reversal-safe: the resulting JE follows GL period-close + reversal policy.
 * Requires a written reason — auditors will ask.
 *
 * Caller wires this through `useGeneralLedger().postJournal` so the same
 * invariants (balance, currency, idempotency) apply.
 */
import type { PostJournalInput } from "./arWriteOffTypes";

export interface WriteOffInput {
  customerId: string;
  amount: number;             // positive dollars
  postingDate: string;        // ISO yyyy-mm-dd
  reason: string;             // ≥10 chars; auditors require justification
  /** Optional reference to the original invoice memo for traceability. */
  invoiceRef?: string;
  badDebtAccountId?: string;  // override; defaults to "6900"
  createdBy?: string;
}

const DEFAULT_BAD_DEBT_ACCOUNT = "6900";

/** Pure builder — returns the PostJournalInput payload. No side effects. */
export function buildWriteOffJournal(input: WriteOffInput): PostJournalInput {
  if (input.amount <= 0) throw new Error("Write-off amount must be positive.");
  if (!input.customerId) throw new Error("Customer is required for write-off.");
  if (!input.reason || input.reason.trim().length < 10) {
    throw new Error("Write-off reason must be at least 10 characters.");
  }
  const acctId = input.badDebtAccountId ?? DEFAULT_BAD_DEBT_ACCOUNT;
  const refTag = input.invoiceRef ? ` · ${input.invoiceRef}` : "";
  return {
    postingDate: input.postingDate,
    description: `Bad-debt write-off — ${input.customerId.replace(/_/g, " ")}: ${input.reason}`,
    source: "adjustment",
    sourceRef: { kind: "ar_write_off", id: `${input.customerId}:${input.postingDate}:${Math.round(input.amount * 100)}` },
    createdBy: input.createdBy,
    lines: [
      {
        accountId: acctId,
        amount: input.amount,
        side: "debit",
        memo: `Bad debt — ${input.customerId.replace(/_/g, " ")}${refTag}`,
        customerId: input.customerId,
      },
      {
        accountId: "1100",
        amount: input.amount,
        side: "credit",
        memo: `Write-off${refTag}`,
        customerId: input.customerId,
      },
    ],
  };
}
